/**
 * Item-relative role presence for directed relationship families.
 *
 * Filters and reports must not collapse both ends of a directed edge into one
 * "Has X" boolean. Group duals (Package / Competing / Cluster) stay membership-
 * based and are not handled here.
 */

import type { ItemRelationshipEdge } from '../types/prioritization/itemRelationships';
import { getCompositionRoleItemIds } from './compositionRelationships';
import { getSequenceRoleItemIds } from './sequenceRelationships';

export interface DirectedRelationshipRolePresence {
  /** Sequence: item has predecessors that must be addressed before it. */
  hasPredecessors: Set<string>;
  /** Sequence: item has dependents waiting on it. */
  hasDependents: Set<string>;
  /** Composition: item is a whole with parts. */
  hasCompositionParts: Set<string>;
  /** Composition: item is a part of a whole. */
  isPartOfComposition: Set<string>;
  /** Elaboration: item is a parent need with possibilities. */
  hasElaborationPossibilities: Set<string>;
  /** Elaboration: item is a possibility on a continuum. */
  isElaborationPossibility: Set<string>;
  /** Contains: item is a reporting parent with children. */
  hasContainsChildren: Set<string>;
  /** Contains: item is a reporting child of a parent. */
  hasContainsParent: Set<string>;
  /** Related: undirected association — either endpoint (no role split). */
  hasRelated: Set<string>;
}

/**
 * Elaboration: source = parent need, target = possibility.
 * Contains: source = parent, target = child.
 */
export function getDirectedRelationshipRolePresence(
  edges: ItemRelationshipEdge[],
): DirectedRelationshipRolePresence {
  const sequence = getSequenceRoleItemIds(edges);
  const composition = getCompositionRoleItemIds(edges);

  const hasElaborationPossibilities = new Set<string>();
  const isElaborationPossibility = new Set<string>();
  const hasContainsChildren = new Set<string>();
  const hasContainsParent = new Set<string>();
  const hasRelated = new Set<string>();

  for (const edge of edges) {
    if (edge.status === 'dissolved') continue;

    if (edge.type === 'elaboration') {
      hasElaborationPossibilities.add(edge.sourceItemId);
      isElaborationPossibility.add(edge.targetItemId);
    } else if (edge.type === 'contains') {
      hasContainsChildren.add(edge.sourceItemId);
      hasContainsParent.add(edge.targetItemId);
    } else if (edge.type === 'related') {
      hasRelated.add(edge.sourceItemId);
      hasRelated.add(edge.targetItemId);
    }
  }

  return {
    hasPredecessors: sequence.hasPredecessors,
    hasDependents: sequence.hasDependents,
    hasCompositionParts: composition.hasParts,
    isPartOfComposition: composition.isPartOf,
    hasElaborationPossibilities,
    isElaborationPossibility,
    hasContainsChildren,
    hasContainsParent,
    hasRelated,
  };
}
